import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Mail, 
  Phone, 
  Trash2, 
  Clock, 
  CheckCircle2,
  XCircle,
  User,
  Copy,
  RefreshCw,
  Shield,
  Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const Workers = () => {
  const { api } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', worker_type: 'operario' });
  const [submitting, setSubmitting] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workersRes, invitationsRes] = await Promise.all([
        api.get('/workers'),
        api.get('/workers/invitations')
      ]);
      setWorkers(workersRes.data);
      setInvitations(invitationsRes.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
      toast.error('Error al cargar los trabajadores');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await api.post('/workers/invite', inviteForm);
      toast.success('Invitación enviada correctamente');
      
      // Show invitation link
      const inviteLink = `${window.location.origin}${response.data.invitation_link}`;
      toast.info(
        <div className="space-y-2">
          <p>Enlace de invitación:</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-slate-100 p-1 rounded">{inviteLink}</code>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(inviteLink);
                toast.success('Enlace copiado');
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>,
        { duration: 10000 }
      );
      
      setDialogOpen(false);
      setInviteForm({ email: '', name: '', worker_type: 'operario' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar la invitación'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    setResendingId(invitationId);
    try {
      const response = await api.post(`/workers/resend-invite/${invitationId}`);
      toast.success(response.data.message);
      
      // Show new invitation link
      const inviteLink = `${window.location.origin}${response.data.invitation_link}`;
      toast.info(
        <div className="space-y-2">
          <p>Nuevo enlace de invitación:</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-slate-100 p-1 rounded">{inviteLink}</code>
            <Button 
              size="sm" 
              variant="ghost"
              onClick={() => {
                navigator.clipboard.writeText(inviteLink);
                toast.success('Enlace copiado');
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>,
        { duration: 10000 }
      );
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al reenviar la invitación'));
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async (workerId) => {
    try {
      await api.delete(`/workers/${workerId}`);
      toast.success('Trabajador eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar el trabajador');
    }
  };

  const getInvitationStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: 'Pendiente',
      accepted: 'Aceptada',
      expired: 'Expirada'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  const getWorkerTypeBadge = (workerType) => {
    if (workerType === 'administracion') {
      return (
        <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Administración
        </Badge>
      );
    }
    return (
      <Badge className="bg-sky-100 text-sky-800 flex items-center gap-1">
        <Wrench className="w-3 h-3" />
        Operario
      </Badge>
    );
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="workers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Trabajadores</h1>
            <p className="text-slate-500">Gestiona tu equipo de trabajo</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-worker-btn">
                <Plus className="w-4 h-4 mr-2" />
                Invitar trabajador
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invitar trabajador</DialogTitle>
                <DialogDescription>
                  Envía una invitación por email para que se una a tu equipo
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre del trabajador</Label>
                  <Input
                    id="name"
                    placeholder="Juan Pérez"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    required
                    data-testid="invite-worker-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="trabajador@email.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    required
                    data-testid="invite-worker-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="worker_type">Tipo de trabajador</Label>
                  <Select 
                    value={inviteForm.worker_type} 
                    onValueChange={(value) => setInviteForm({ ...inviteForm, worker_type: value })}
                  >
                    <SelectTrigger data-testid="worker-type-select">
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operario">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-4 h-4" />
                          <span>Operario</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="administracion">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          <span>Administración</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {inviteForm.worker_type === 'administracion' 
                      ? 'Tendrá acceso completo a clientes, trabajos, reportes y estadísticas.'
                      : 'Solo verá sus propios trabajos asignados.'}
                  </p>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={submitting} data-testid="send-invite-btn">
                    {submitting ? 'Enviando...' : 'Enviar invitación'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Workers List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Equipo activo ({workers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {workers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aún no tienes trabajadores en tu equipo</p>
                <p className="text-sm mt-1">Invita al primer miembro de tu equipo</p>
              </div>
            ) : (
              <div className="space-y-4">
                {workers.map((worker, index) => (
                  <motion.div
                    key={worker.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors gap-3"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        worker.worker_type === 'administracion' ? 'bg-purple-100' : 'bg-sky-100'
                      }`}>
                        {worker.worker_type === 'administracion' 
                          ? <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                          : <User className="w-5 h-5 sm:w-6 sm:h-6 text-sky-600" />
                        }
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-slate-900 truncate">{worker.name}</h4>
                          {getWorkerTypeBadge(worker.worker_type)}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{worker.email}</span>
                          </span>
                          {worker.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {worker.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50 self-end sm:self-auto">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar trabajador?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. El trabajador perderá acceso a la plataforma.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(worker.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invitations */}
        {invitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invitaciones</CardTitle>
              <CardDescription>Historial de invitaciones enviadas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-slate-50 gap-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-slate-900 truncate">{invitation.name}</h4>
                        {invitation.worker_type === 'administracion' && (
                          <Badge className="bg-purple-100 text-purple-800 text-xs">
                            Admin
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{invitation.email}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
                      <span className="text-xs text-slate-400">
                        {new Date(invitation.created_at).toLocaleDateString()}
                      </span>
                      {getInvitationStatusBadge(invitation.status)}
                      {invitation.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResendInvitation(invitation.id)}
                          disabled={resendingId === invitation.id}
                          data-testid={`resend-invite-${invitation.id}`}
                          className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                        >
                          {resendingId === invitation.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Reenviar</span>
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </OwnerLayout>
  );
};

export default Workers;
